/** @module csi/html */
define([
	"jquery",
	"dojo/_base/window",
	"dojo/Deferred",
	"dojo/dom",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-style",
	"dojo/query",
	"dijit/_WidgetBase",
	"dijit/focus",
	"dijit/registry",
	"designSystem/utilities/prevent-default-behavior",
	"underscore",
	"csi",
	"csi/util",
	"csi/utility/htmlUtility",
	"csi/utility/stringUtility",

	// extend NodeList to support more methods
	"dojo/NodeList-dom",
	"dojo/NodeList-manipulate"
], function(
	$,
	win,
	Deferred,
	dom,
	domClass,
	domConstruct,
	domStyle,
	query,
	_WidgetBase,
	dijitFocus,
	registry,
	{preventDefaultBehaviour},
	_,
	csi,
	util,
	htmlUtility,
	{
		purifyText,
		stringSubstitute,
	}
) {

	const MIN_SCROLLBAR_PADDING = 10;

	var CSS_BROWSE = "browse";
	var CSS_HIDE = "hideFromView";
	var CSS_MATERIAL_ICON = "material-icons";
	var CSS_RICH_TEXT = "fr-view";
	var CSS_NO_DISPLAY = "nodisplay";
	var DATA_CSI_PREFIX = "data-csi-";
	const DIV = "div";
	var HTML_DASH = "-\u200B";
	var HTML_SPACE = "\u00A0";
	var NEWLINE = "\n";
	var POS_AROUND = "around";
	var RE_DASH = /\-/g;
	var RE_NEWLINE = /(\r\n|\n|\\n)/g;

	var EM = "em";
	var PX = "px";
	const CSS_EXCEL_WRAP = "csi-excel-wrap";
	var WIDTH = "width";

	// Object: map of registered data attribute cast functions to cast string back to source type (possible values are Boolean, Number - String is default)
	var _dataAttributeCast = {};

	// Object: map of registered data attribute values - used to ensure that there are no duplicates
	var _dataAttributeNames = {};

	/** @property {DOMNode} _focusInput - an invisible temporary target DOM just to be focus when we want to remove the focus from another DOM */
	var _focusInput;

	/** @property {DOMNode} _lastFileInput - file input node used for selecting files */
	var _lastFileInput;

	/** Boolean: _isPrinting - for checking if printing */
	let _isPrinting;

	/** Number: _printWidth - width for page content */
	let _printWidth;

	// DOMNode: node used as root node for sanitizing rich text
	var _sanitizeDiv = domConstruct.create("div");

	/**
	 * Variable for showWait(), store domNode for covering div
	 * @property {domNode} waitDiv
	 */
	var waitDiv = null;

	/**
	 * Variable for showWait(), timeout id for covering div animation
	 * @property {Object} showWaitTimeoutId
	 */
	var showWaitTimeoutId = null;

	/**
	 * Variable for showWait(), timeout id for delay before showing the layer
	 * @property {Object} showWaitDelayTimeoutId
	 */
	var showWaitDelayTimeoutId = null;

	/**
	 * Variable for showWait(), flag for timeout, if wait div have to be destroyed
	 * @property {boolean} bShowWaitDestroy
	 */
	var bShowWaitDestroy = false;

	const {
		escapeDataAttributeSelectorValue,
		getHorizontalScrollbarHeight,
		getVerticalScrollbarWidth,
		preciseHeight,
		preciseWidth,
		purifyForHtml,
		registerPrintAttribute,
	} = htmlUtility;

	function _castBooleanDataAttribute(v) {
		// be careful with Boolean casts: Boolean("false") != false
		return (v === true) || (v === "true");
	}

	/**
	 * Return true if no content in DOMNode, including if it just contains EMPTY_CHAR
	 * @param {DOMNode} e
	 * @return {boolean}
	 */
	function isEmpty(e) {
		return !e.firstChild || (e.innerHTML == csi.EMPTY_CHAR);
	}

	function _sanitizeRichTextNode(/*DOMNode*/domNode) {
		// summary: rich text widget takes computed styles and places directly on node, so any classes
		// can be removed, as well as iconEditable styling
		domClass.remove(domNode, CSS_RICH_TEXT + " attrString iconEditable");
		domStyle.set(domNode, {
			backgroundImage: "",
			backgroundPosition: "",
			backgroundRepeat: "",
			padding: ""
		});
	}

	/**
	 * Reset node content to empty - pass true in second parameter if content could contain widgets
	 * @param {DOMNode} domNode
	 * @param {boolean} bWidgets
	 */
	function clear(domNode, bWidgets) {
		if (domNode && domNode.firstChild) {
			if (bWidgets) {
				destroyWidgets(domNode);
			}

			domConstruct.empty(domNode);
		}
	}

	/**
	 * Wrapper for domConstruct.create but with support for additional parameter innerText and multiLineText that will handle
	 * special characters that basic innerHTML will not, and special position "around" which will wrap given node with new node
	 * @param {HTMLElement|string} tag
	 * @param {Object} params
	 * @param {HTMLElement} refNode
	 * @param {string} [pos]
	 * @return {HTMLElement}
	 */
	function create(tag, params, refNode, pos) {
		const {
			dataset,
			innerText: sltext,
			multiLineText: mltext,
			...otherParams
		} = params || {};

		let childNode;

		if (pos == POS_AROUND) {
			childNode = refNode;
			pos = "replace";
		}

		const element = domConstruct.create(tag, otherParams, refNode, pos);

		if (childNode) {
			element.appendChild(childNode);
		}

		_.forEach(dataset, (value, key) => {
			setDataAttribute(element, key, value);
		});

		// if existing node passed in and setting text, then clear children first
		if ((element == tag) && ((sltext !== undefined) || (mltext !== undefined))) {
			clear(element, true);
		}

		if ((sltext !== undefined) || (mltext === null)) {
			text(element, sltext);
		} else if (mltext) {
			multiLineText(element, mltext);
		}

		return element;
	}

	/**
	 * Check the current key if it's ctrl key
	 * @param {Event} e
	 * @return {boolean}
	 */
	function ctrlKeyPressed(e) {
		return e.ctrlKey || e.metaKey;
	}

	/**
	 * Destroy element's children, making sure to clear widgets from registry
	 * @param {DOMNode} domNode
	 */
	function destroyChildren(domNode) {
		clear(domNode, true);
	}

	/**
	 * Destroy element including any child widgets
	 * @param {DOMNode} domNode
	 */
	function destroyElement(domNode) {
		if (domNode) {
			destroyWidgets(domNode);
			domConstruct.destroy(domNode);
		}
	}

	/**
	 * Destroy widgets contained by given element
	 * @param {DOMNode} domNode
	 */
	function destroyWidgets(domNode) {
		_.each(registry.findWidgets(domNode), function(w) {
			w.destroyRecursive();
		});
	}

	/**
	 * Disable Page Content scroll bar to appear during transition
	 * @param {boolean} bDisable - if true, scrolling will be disabled
	 */
	function disablePageContentScrolling(bDisable) {
		domStyle.set(pageContent(), "overflow", bDisable ? "hidden" : "");
		domStyle.set(pageContentScroll(), "overflow", bDisable ? "hidden" : "");
	}

	/**
	 * Download file to client
	 * @param {string} filename
	 * @param {Blob} blob
	 */
	function download(filename, blob) {
		// Implementation inspired by this post:
		// https://nehalist.io/downloading-files-from-post-requests/
		var a = document.createElement('a');
		var url = window.URL.createObjectURL(blob);
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		_.delay(function() {
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		}, 1000);
	}

	/**
	 * Return true if given node overflows its vertical bounds, within the given tolerance (if any)
	 * @param {DOMNode} domNode
	 * @param {number} tolerance - number of pixels allowed to overflow without returning true
	 * @return {boolean}
	 */
	function elementOverflows(domNode, tolerance) {
		return domNode.scrollHeight - domNode.clientHeight > (tolerance || 0);
	}

	/**
	 * Return true if given node overflows its horizontal bounds, within the given tolerance (if any)
	 * @param {DOMNode} domNode
	 * @param {number} tolerance - number of pixels allowed to overflow without returning true
	 * @return {boolean}
	 */
	function elementOverflowsWidth(domNode, tolerance) {
		return domNode.scrollWidth - domNode.clientWidth > (tolerance || 0);
	}

	/**
	 * Focus given node
	 * @param {DOMNode} domNode
	 */
	function focus(domNode) {
		dijitFocus.focus(domNode);
	}

	/**
	 * Function will set width and height of the node to it's current offsetWidth and offsetHeight,
	 *  or to "", depend on bFreeze parameter
	 * Used to prevent node resize (usually to prevent flicker)
	 * @param {Object} domNode
	 * @param {boolean} bFreeze - true to freeze, false to unfreeze
	 */
	function freezeDomNode(domNode, bFreeze) {
		if (bFreeze) {
			var rect = domNode.getBoundingClientRect();
			domStyle.set(domNode, {
				width: rect.width + "px",
				height: rect.height + "px"
			});
		} else {
			domStyle.set(domNode, {
				width: "",
				height: ""
			});
		}
	}

	function getChildWidgets(/*DOMNode*/domNode) {
		// summary: return all widgets under given node
		return registry.findWidgets(domNode);
	}

	function getDataAttributeName(/*String*/attrName) {
		// summary: return full name of data attribute
		return DATA_CSI_PREFIX + attrName;
	}

	/**
	 * Return array of files from input
	 * @param {DOMNode} input
	 * @return {File[]}
	 */
	function getInputFileArray(input) {
		return _.map(input.files, _.identity);
	}

	/**
	 * Hide given node
	 * @param {DOMNode} domNode
	 */
	function hide(domNode) {
		domNode && domClass.add(domNode, CSS_NO_DISPLAY);
	}

	/**
	 * Add inline class to CSS to help it align vertically (normally used for spans)
	 * @param {string} css
	 * @return {string}
	 */
	function inlineCss(css) {
		return "dijitInline" + (css ? (" " + css) : "");
	}

	/**
	 * Return true if given object is a Dojo Widget
	 * @param {any} obj
	 * @return {boolean}
	 */
	function isWidget(obj) {
		return obj instanceof _WidgetBase;
	}

	/**
	 * Focus on the invisible element so that the current focus goes away
	 */
	function removeFocus() {
		_focusInput = _focusInput || create("input", {
			className: "csi-offscreen"
		}, document.body);

		_focusInput.focus();
	}

	/**
	 * process mouse click event and open href in current or new window and
	 * call preventDefaultBehaviour to prevent further event propagation
	 * @param {MouseEvent} en
	 * @param {string} href
	 */
	function openWindowOnMouseClick(ev, url) {
		// dojo's menu item (see dijit/_MenuBase=>postCreate() ) tries to traps the normal click behavior, 
		// it is not always working anymore, so we have to stop event 
		preventDefaultBehaviour(ev);
		if (this.ctrlKeyPressed(ev)) {
			// Ctrl+Click should open link in a new window			
			window.open(url);
		} else {
			// navigate to url in current window
			window.location = url;
		}
	}

	/**
	 * Remove node from DOM, and returning the same node
	 * @param {DOMNode} domNode
	 * @return {DOMNode}
	 */
	function orphan(domNode) {
		return (domNode && domNode.parentNode) ? domNode.parentNode.removeChild(domNode) : domNode;
	}

	/**
	 * Convert value in pixels to string value used for styling
	 * @param {number} x - pixel value
	 * @param {number} [pxToEm] - pixel to em ratio if want to use "em" units
	 * @return {string}
	 */
	function pixelsToStyleValue(px, pxToEm) {
		return pxToEm ? `${px * pxToEm}${EM}` : `${px}${PX}`;
	}

	/**
	 * Return ratio from pixels to given unit
	 * @param {string} unit
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function pixelTo(unit, domNode) {
		const div = create("div", {style: "width:100" + unit}, domNode);
		const w = domStyle.get(div, WIDTH);
		domConstruct.destroy(div);
		return 100 / w;
	}

	/**
	 * Remove data attribute from given domNode
	 * @param {HTMLElement} domNode 
	 * @param {string} attrName 
	 */
	function removeDataAttribute(domNode, attrName) {
		if (domNode) {
			domNode.removeAttribute(DATA_CSI_PREFIX + attrName);
		}
	}

	/**
	 * Resize child widgets of given node
	 * @param {DOMNode} domNode
	 */
	function resizeChildren(domNode) {
		_.each(getChildWidgets(domNode), function(w) {
			if (w.resize) {
				w.resize();
			}
		});
	}

	/**
	 * Add CSS variable to given dom node
	 * @param {DOMNode} domNode
	 * @param {string} variableName
	 * @param {any} value
	 * @param {boolean} [inPixels]
	 */
	function setCSSVariable(domNode, variableName, value, inPixels) {
		// add PX to value of 0 or else it won't work with "calc" expressions
		if (inPixels && !_.isString(value)) {
			value += PX;
		}

		domNode.style.setProperty(`--${variableName}`, value);
	}

	/**
	 * Add attribute to given domNode if non-null value
	 * @param {HTMLElement} domNode 
	 * @param {string} attrName 
	 * @param {any} value 
	 */
	function setDataAttribute(domNode, attrName, value) {
		if (domNode) {
			if (value == null) {
				removeDataAttribute(domNode, attrName);
			} else {
				const cast = _dataAttributeCast[attrName];
				if (cast) {
					value = cast(value);
				}

				domNode.setAttribute(DATA_CSI_PREFIX + attrName, value);
			}
		}
	}

	/**
	 * Set visibility of node to visible or hidden
	 * @param {DOMNode|_WidgetBase} e
	 * @param {boolean} bShow
	 */
	function setVisibility(e, bShow) {
		if (e && isWidget(e)) {
			e = e.domNode;
		}

		if (e && (e.nodeType == 1)) {
			e.style.visibility = bShow ? "" : "hidden";
		}
	}

	/**
	 * Show given node
	 * @param {DOMNode} domNode
	 */
	function show(domNode) {
		domNode && domClass.remove(domNode, CSS_NO_DISPLAY);
	}

	/**
	 * Open file browser
	 * @param {boolean} multiple
	 * @param {string} [accept] - provides restriction on what all files can be selectable
	 * @return {Deferred}
	 */
	function _selectFiles(multiple, accept) {
		// remove last file input from DOM - since only one file browser can ever be open at one time, no need to keep previous;
		// we can't remove it right after use as there is no good way to detect when a file browser is cancelled
		if (_lastFileInput) {
			orphan(_lastFileInput);
		}

		const dfd = new Deferred();
		const {body} = document;
		const input = _lastFileInput = create("input", {
			type: "file",
			style: "position: absolute; left: -1000px; top: 0",
			onchange: function() {
				dfd.resolve(getInputFileArray(this));
			}
		}, body);

		if (multiple) {
			input.setAttribute("multiple", true);
		}

		if (accept) {
			input.setAttribute("accept", accept);
		}

		/**
		 * C8-51204
		 * Unfortunately, there is no direct way to detect when the user clicks "cancel" on the upload dialog.
		 * However, we can employ a workaround to infer the cancellation by utilizing focus and mousemove events.
		 * Before opening the upload files dialog, set up a focus event listener on the window.
		 * When this event handler is triggered, it can't be assumed that cancel was clicked - yet - because the focus triggers slightly before a file is added to input.files.
		 * There has to be an additional mousemove event listener added to the body.
		 * If, at this point, input.files.length is 0, we can safely assume that cancel was clicked.
		 * In the case of a successful upload, the focus event handler would have been removed before the window gains focus.
		 * TODO: Fix open sometimes firing with input.files.length === 0 & FireFox and we can remove global bInterrupt and bKeepViewEnabled workaround
		 */
		// function onMouseMoveAfterFocusWindow() {
		// 	if (input.files.length === 0) {
		// 		dfd.resolve([])
		// 	}
		// }

		// function onFocusWindowAfterInputDialogCloses() {
		// 	body.addEventListener("mousemove", onMouseMoveAfterFocusWindow, {once: true});
		// };

		// input.addEventListener('click', () => {
		// 	window.addEventListener('focus', onFocusWindowAfterInputDialogCloses, {once: true});
		// });

		$(input).click();

		return dfd;
	}

	/**
	 * Open file browser to browse for a single file, returning a Deferred object that will be resolved once files are selected;
	 * NOTE: if the file browser is cancelled, the deferred will not be rejected automatically - it is up to caller to decide when that happens
	 * @param {string} [accept] - provides restriction on what all files can be selectable
	 * @return {Deferred}
	 */
	function selectFile(accept) {
		return _selectFiles(false, accept);
	}

	/**
	 * Open file browser to browse for multiple files, returning a Deferred object that will be resolved once files are selected
	 * NOTE: if the file browser is cancelled, the deferred will not be rejected automatically - it is up to caller to decide when that happens
	 * @param {string} [accept] - provides restriction on what all files can be selectable
	 * @return {Deferred}
	 */
	function selectFiles(accept) {
		return _selectFiles(true, accept);
	}

	/**
	 * Set style on DOM node in pixels
	 * @param {DOMNode} domNode
	 * @param {string} style - style to set, such as height or width
	 * @param {number|string} pixels
	 */
	function setPixels(domNode, style, pixels) {
		domStyle.set(domNode, style, pixels ? (pixels + PX) : pixels);
	}

	/**
	 * Set dimension values for each child of given parent node
	 * @param {DOMNode} parent
	 * @param {string} style - dimension to set, such as height or width
	 * @param {number[]} values - specified in pixels
	 */
	function setPixelsForChildren(parent, style, values) {
		_.each(parent.children, function(domNode, i) {
			if (values[i]) {
				setPixels(domNode, style, values[i]);
			}
		});
	}

	/**
	 * Return DOMNode representing the bottom pane of the page
	 * @return {DOMNode}
	 */
	function pageBottom() {
		return dom.byId("pageBottom");
	}

	/**
	 * Return DOMNode representing the center pane of the page
	 * @return {DOMNode}
	 */
	function pageCenter() {
		return dom.byId("pageCenter");
	}

	/**
	 * Return widget that controls splitting pageCenter node
	 * @return {DOMNode}
	 */
	function pageCenterSplit() {
		return dom.byId("pageCenterSplit");
	}

	/**
	 * Return DOMNode holding center page content
	 * @return {DOMNode}
	 */
	function pageContent() {
		return dom.byId("pageContent");
	}

	/**
	 * Return center page scroll node
	 * @return {DOMNode}
	 */
	function pageContentScroll() {
		return dom.byId("pageContentScroll") || pageContent();
	}

	/**
	 * Return SplitPane holding help pane
	 * @return {SplitPane}
	 */
	function pageSplit() {
		return registry.byId("pageCenter");
	}

	/**
	 * Return true if a vertical scrollbar is scrolled to bottom
	 * @param {DOMNode} element
	 * @return {boolean}
	 */
	function isScrolledToBottom(element) {
		if (element) {
			const {offsetHeight, scrollHeight, scrollTop} = element;
			return scrollTop && (scrollTop === (scrollHeight - offsetHeight));
		}

		return false;
	}

	/**
	 * Return true if a vertical scrollbar is not scrolled (scrolled to top)
	 * @param {DOMNode} element
	 * @return {boolean}
	 */
	function isScrolledToTop(element) {
		return element && !element.scrollTop;
	}

	/**
	 * Return true if a horizontal scrollbar is showing for given element
	 * @param {DOMNode} element
	 * @return {boolean}
	 */
	function isScrollingHorizontally(element) {
		return element && (element.scrollWidth > element.clientWidth);
	}

	/**
	 * Return true if a vertical scrollbar is showing for given element
	 * @param {DOMNode} element
	 * @return {boolean}
	 */
	function isScrollingVertically(element) {
		return element && (element.scrollHeight > element.clientHeight);
	}

	/**
	 * Find the available height below a given node to the bottom of the pageCenter area to avoid scrolling
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getAvailableHeight(domNode) {
		var pageNode = $(pageCenter());
		var pageBottom = pageNode.height() + pageNode.offset().top;
		var nodeTop = $(domNode).offset().top + pageNode.scrollTop();
		return pageBottom - nodeTop;
	}

	/**
	 * Find the available width on the right of a given node to the right of the pageCenter area to avoid scrolling
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getAvailableWidth(domNode) {
		var pageNode = pageContent();
		return pageNode.clientWidth - domNode.offsetLeft - getVerticalScrollbarWidth();
	}

	/**
	 * Find the available height below a given node to the bottom of the pageCenter area, allowing enough extra space for the
	 * horizontal scrollbar to appear if necessary
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getAvailableHeightWithScrollbarAllowance(domNode) {
		const scrollNode = pageContentScroll();
		var top = $(domNode).offset().top + $(scrollNode).scrollTop();
		var bottom = $(pageBottom()).offset().top;

		// subtract extra 2 pixels just in case; in case of Mac, at least provide 10px allowance when scrollbars not always visible
		return bottom - top - Math.max(getHorizontalScrollbarHeight(), MIN_SCROLLBAR_PADDING) - 2;
	}

	/**
	 * Wrapper for DOMNode.getBoundingClientRect
	 * @param {DOMNode} domNode
	 * @return {DOMRect}
	 */
	function getBoundingRectAndPosition(domNode) {
		return domNode.getBoundingClientRect();
	}

	/**
	 * Find element containing given element if it is scrolling either horizontally or vertically
	 * @param {HTMLElement} element
	 * @return {boolean}
	 */
	function inVerticalScrollingContainer(element) {
		while (element = element.parentNode) {
			if (element.scrollHeight > element.clientHeight) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Convert hexadecimal color value to RGB triple
	 * @param {string} hex
	 * @return {number[]}
	 */
	function HEXtoRGB(hex) {
		// Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
		var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
		hex = hex.replace(shorthandRegex, function(m, r, g, b) {
			return r + r + g + g + b + b;
		});

		var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
	}

	/**
	 * Convert a string with possibility of newline characters ('\n') into one or more text nodes separated by line breaks (<br>)
	 * @param {DOMNode} eParent
	 * @param {string} s
	 */
	function multiLineText(eParent, s) {
		var a = s.replace(RE_NEWLINE, NEWLINE).split(NEWLINE);
		var n = a.length - 1;

		for (var i = 0; i <= n; i++) {
			text(eParent, a[i]);

			if (i < n) {
				eParent.appendChild(document.createElement("br"));
			}
		}
	}

	/**
	 * Detect type of line end and split multiline text to array of strings
	 */
	function multilineTextToArray(text) {
		return text.replace(RE_NEWLINE, NEWLINE).split(NEWLINE);
	}

	/**
	 * Create single text node
	 * @param {DOMNode} eParent
	 * @param {string} s
	 * @param {boolean} bNoWrap
	 */
	function text(eParent, s, bNoWrap) {
		if (!s) {
			s = HTML_SPACE;
		} else if (_.isString(s)) {
			// force wrapping at dashes while printing for alphanumeric strings with dashes - otherwise can force tables to be too wide
			if (!bNoWrap && _isPrinting) {
				s = s.replace(RE_DASH, HTML_DASH);
			}
		}

		eParent.appendChild(document.createTextNode(s));
	}

	function setPrintStatus(status) {
		_isPrinting = status;
	}

	function setPrintWidth(width) {
		_printWidth = width;
	}

	var _html = {
		// common classes
		CSS_EXCEL_WRAP,
		CSS_ACTION: "csiAction",
		CSS_ACTION_TOGGLE: "Toggle",
		CSS_BROWSE: CSS_BROWSE,
		CSS_BUTTON_LINK: "csi-link-btn",
		CSS_BUTTON_PRIMARY: "csi-primary-btn",
		CSS_BUTTON_SECONDARY: "csi-secondary-btn",
		CSS_DISABLED_COLOR: "csi-disabled-color",
		CSS_DISABLED_SELECTION: "csi-disabled-selection",
		CSS_DND_HANDLE: "csi-dnd-handle",
		CSS_DROPDOWN_ACTION: "csi-dropdown-action-item",
		CSS_DROPDOWN_GOTODIALOG: "csi-dropdown-gotodialog-item",
		CSS_ENABLE_ON_CHANGE: "csiEnableOnChange",
		CSS_EXPORT: "csiExport",
		CSS_HIDE: CSS_HIDE,
		CSS_ICON: "csiActionIcon",
		CSS_INVALID_SELECTION: "csi-invalid-selection",
		CSS_MATERIAL_ICON: CSS_MATERIAL_ICON,
		CSS_NO_DISPLAY: CSS_NO_DISPLAY,
		CSS_NO_EXPORT: "noexport",
		CSS_NOT_APPLICABLE: "csi-bg-not-applicable",
		CSS_PAGE: "csiPageBlock",
		CSS_PAGE_BREAK: "csiPageBreak",
		CSS_PASTE_FOCUS: "csi-paste-focus",
		CSS_RICH_TEXT: CSS_RICH_TEXT,
		CSS_VIEW: "csi-view",
		CSS_VIEW_TITLE: "csi-view-title",

		// exposed constants
		EM: EM,
		HEIGHT: "height",
		HIDDEN: "hidden",
		LEFT: "left",
		MAXHEIGHT: "maxHeight",
		MINHEIGHT: "minHeight",
		MAXWIDTH: "maxWidth",
		MINWIDTH: "minWidth",
		POS_AROUND: POS_AROUND,
		PX: PX,
		SPACE: HTML_SPACE,
		TAB: "\u00A0\u00A0\u00A0\u00A0",
		TOP: "top",
		WIDTH: WIDTH,

		// regular expression used to extract element's inner text
		INNERTEXT_REGEXP: /<\/?[^>]+>/gi,

		/**
		 * Add appropriate dimension to each number value in object
		 * @param {DOMNode} domNode
		 * @param {Object} styles
		 * @param {boolean} [useEms] - if true, use em rather than px as unit to allow node to adjust to font sizes scaling (percentage)
		 * @return {Object}
		 */
		addStyleUnits: function(domNode, styles, useEms) {
			var unit = useEms ? EM : PX;
			var ratio = useEms ? pixelTo(unit, domNode) : 1;

			_.each(styles, function(value, key) {
				if (_.isNumber(value)) {
					styles[key] = util.truncate(value * ratio, 0.01) + unit;
				}
			});

			return styles;
		},

		attributeSelector: function(/*String*/attr, /*String?*/value) {
			// summary: return text to use as selector for given attribute with optional value
			return "[" + attr + ((value == null) ? "]" : ('="' + value) + '"]');
		},

		/**
		 * Converts Base64 file data to blob
		 * @param {string} b64Data
		 * @param {string} fileMime -  mime type of file (eg. "image/jpeg")
		 * @param {number} [sliceSize] - byteArray size to create the blob
		 */
		b64toBlob: function(b64Data, fileMime, sliceSize) {
			fileMime = fileMime || '';
			sliceSize = sliceSize || 512;

			var byteCharacters = atob(b64Data);
			var byteArrays = [];

			for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
				var slice = byteCharacters.slice(offset, offset + sliceSize);

				var byteNumbers = new Array(slice.length);
				for (var i = 0; i < slice.length; i++) {
					byteNumbers[i] = slice.charCodeAt(i);
				}

				var byteArray = new Uint8Array(byteNumbers);

				byteArrays.push(byteArray);
			}

			var blob = new Blob(byteArrays, {
				type: fileMime
			});
			return blob;
		},

		clearWidgets: function(/*DOMNode*/e) {
			// summary: remove widgetIds from DOM - mostly used for printing when there is no interaction
			query("[widgetId]", e).forEach(function(domNode) {domNode.setAttribute("widgetId", "");});
		},

		cloneElement: function(/*DOMNode*/e) {
			// summary: clone element, but remove widget ids from copied nodes so that dijit does not get confused -- dijit copying is not yet supported
			if (e) {
				e = e.cloneNode(true);

				if (e.getAttribute("widgetId")) {
					e.setAttribute("widgetId", "");
				}

				this.clearWidgets(e);
			}

			return e;
		},


		/**
		 * Convert base 64 data to uploadable file format
		 * @param {base64Data} base64Data - base 64 data of file (eg. base 64 data for canvas HTMLCanvasElement.toDataURL)
		 * @param {string} fileName
		 * @param {string} fileMime
		 * @return {File} file
		 */
		convertBase64toFile: function(base64Data, fileName, fileMime) {
			var data = base64Data.split(',')[1];
			var fileBlob = this.b64toBlob(data, fileMime);

			return new File([fileBlob], fileName, {
				type: fileMime
			});
		},

		/**
		 * Convert image to Base 64 data
		 * @param {image} img
		 * @return {Base64} dataURL
		 */
		convertImagetoBase64: function(img) {
			var canvas = document.createElement("canvas");
			canvas.width = img.width;
			canvas.height = img.height;
			var ctx = canvas.getContext("2d");
			ctx.drawImage(img, 0, 0);
			var dataURL = canvas.toDataURL("image/png");

			return dataURL;
		},

		/**
		 * creates browser events, handles older versions of ie
		 * @param {string} eventName
		 */
		createNewEvent: function(eventName) {
			var event;
			if (typeof (Event) === 'function') {
				event = new Event(eventName);
			} else {
				event = document.createEvent('Event');
				event.initEvent(eventName, true, true);
			}
			return event;
		},

		createRowAnchor: function(domNode, url, title) {
			// summary: creates an empty anchor tag and places it on the domNode
			// used to facilitate right click and open in new tab in certain conditions
			var nodes = query(".csi-card-anchor", domNode).attr("href", url);
			if (nodes && title) {
				nodes.attr("title", title);
			}
		},

		destroyRange: function(/*DOMNode*/eFirst, /*DOMNode?*/eLast, /*Function?*/filter) {
			// summary: destroy elements in range from first to last based on optional
			// filter function; if last not specified, then it will go to last sibling
			var e;

			if (!eFirst) return;

			if (!eLast && eFirst.parentNode)
				eLast = eFirst.parentNode.lastChild;

			while (eFirst && (eFirst != eLast)) {
				e = eFirst.nextSibling;

				if (!filter || filter(eFirst)) {
					destroyElement(eFirst);
				}

				eFirst = e;
			}

			if (eLast && (!filter || filter(eLast))) {
				destroyElement(eLast);
			}
		},

		/**
		 * Return true if widget exists and is not already destroyed, or in the process of being destroyed
		 * @param {_Widget} w
		 * @return {boolean}
		 */
		exists: function(w) {
			return w && !w._destroyed && !w._beingDestroyed;
		},

		/**
		 * Return element width and height.
		 * @param {Object} element - element to evaluate
		 * @param {string} [containerClassName] - extra css classes (space separated) to apply to container (element size will be affected by styling)
		 * @param {string} [elementClassName] - extra css classes (space separated) to apply to element
		 * @param {Object} [node] - dom node to use as parent node. Specify this if element size might be affected by node styles.
		 *		If not specified, document body node will be used as parent
		 * @return {Object} - {width, height}
		 */
		getElementSize: function(element, containerClassName, elementClassName, node) {
			var div = document.createElement("div");
			div.style.overflow = "visible";
			setVisibility(div, false);
			div.style.position = 'absolute';
			div.style.width = 'auto';
			div.style.height = 'auto';
			div.className = containerClassName || '';
			node = node || document.body;
			node.appendChild(div);
			element = this.cloneElement(element);
			element.className += elementClassName ? ' ' + elementClassName : '';
			var elem = div.appendChild(this.cloneElement(element));
			var width = elem.offsetWidth;
			var height = elem.offsetHeight;
			node.removeChild(div);
			return {width: width, height: height};
		},

		/**
		 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
		 *
		 * @param {String} text The text to be rendered.
		 * @param {String} [font] The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
		 *
		 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
		 */
		getTextWidth: function(text, font) {
			const CSI_STANDARD_FONT = "400 12px Centric8";
			font = font || CSI_STANDARD_FONT;

			// re-use canvas object for better performance
			var canvas = this.getTextWidthCanvas || (this.getTextWidthCanvas = document.createElement("canvas"));
			var context = canvas.getContext("2d");
			if (font) {
				context.font = font;
			}
			var metrics = context.measureText(text);
			return metrics.width;
		},

		/**
		 * Query for dom nodes given selector components under optional root node
		 * @param {string[]} components - strings to join together to form dom query string - use html.attributeSelector or html.getDataAttributeSelector for components
		 * @param {DOMNode} [domRoot] - root DOM node under which to query for nodes matching query
		 * @param {boolean} bOne - if true, then only return first result found
		 * @return {Widget|Widget[]}
		 */
		queryDom: function(components, domRoot, bOne) {
			var nl = query(components.join(""), domRoot);
			return bOne ? nl[0] : nl;
		},

		/**
		 * Query for widgets given selector components under optional root node
		 * @param {string[]} components - strings to join together to form dom query string - use html.attributeSelector or html.getDataAttributeSelector for components
		 * @param {DOMNode} [domRoot] - root DOM node under which to query for nodes matching query
		 * @param {boolean} bOne - if true, then only return first result found
		 * @return {Widget|Widget[]}
		 */
		queryDomForWidgets: function(components, domRoot, bOne) {
			var nl = query(components.join(""), domRoot).map(registry.byNode);
			return bOne ? nl[0] : nl;
		},

		registerDataAttribute: function(/*String*/attrConstant, /*String*/attrName, /*Function?*/cast, /*Boolean*/bForPrint) {
			// summary: register data attribute to apply to DOM in certain situations - ensure they are unique to avoid confusion;
			// cast function can be specified to transform value when getting and setting value - this is especially necessary since
			// attributes can only store string values (common cast functions are Boolean and Number)
			if (attrConstant && this[attrConstant]) {
				console.error("Duplicate data attribute constant: " + attrConstant + " = " + attrName);
				return null;
			}

			if (attrName && _dataAttributeNames[attrName]) {
				console.error("Duplicate data attribute name: " + attrConstant + " = " + attrName);
				return null;
			}

			const dataAttrName = getDataAttributeName(attrName);

			if (bForPrint) {
				registerPrintAttribute(dataAttrName);
			}

			_dataAttributeCast[attrName] = (cast === Boolean) ? _castBooleanDataAttribute : cast;
			_dataAttributeNames[attrName] = 1;
			this[attrConstant] = attrName;

			return dataAttrName;
		},

		unregisterDataAttribute: function(/*String*/attrConstant) {
			// summary: unregister data attribute (keep for testing)
			var attrName = this[attrConstant];
			delete this[attrConstant];
			delete _dataAttributeCast[attrName];
			delete _dataAttributeNames[attrName];
		},

		stringToDom: function(/*String*/template, /*String[]?*/params, /*DOMNode?*/domNode, /*String?*/pos) {
			// summary: construct dom from template and possible params
			var n = domConstruct.toDom(params ? stringSubstitute(template, params) : template);

			if (domNode) {
				domConstruct.place(n, domNode, pos);
			}

			return n;
		},

		mouseInBox: function(/*Number*/x, /*Number*/y, /*Object*/box) {
			// summary: return true if given point is in box
			return ((x >= box.l) && (x <= box.l + box.w) && (y >= box.t) && (y <= box.t + box.h));
		},

		/**
		 * Get whether mouse is in node's bounds or not
		 * Pass in event to indicate whether element directly under mouse must equal the node
		 * @param {number} x - horizontal bound
		 * @param {number} y - vertical bound
		 * @param {HTMLElement} node - domNode to search whether or not mouse is inside
		 * @param {MouseEvent} e - optional event - useful for when objects overlap
		 */
		mouseInNode: function(x, y, node, e) {
			if (e && !node.contains(e.target)) {
				return false;
			}

			// summary: return true if given point lies within given node
			const rect = node.getBoundingClientRect();
			return (x >= rect.left) && (x <= rect.right) && (y >= rect.top) && (y <= rect.bottom);
		},

		setClass: function(e, c) {
			if (e) e.className = c;
		},

		getClass: function(e) {
			if (e) {
				if (e.getAttribute && e.getAttribute('class'))
					return e.getAttribute('class');

				return e.className;
			}

			return null;
		},

		getComputedStyle: function(e, prop, valueInherit) {
			var v;

			while (e) {
				if (e.nodeType == 1) {
					v = domStyle.get(e, prop);
					if (v != valueInherit) return v;
				}

				e = e.parentNode;
			}

			return null;
		},

		getElementAction: function(/*DOMNode*/domNode, /*Boolean*/bThisElement) {
			// summary: get action id applied to domNode or any of its parents
			return this.getDataAttribute(domNode, this.DATA_ACTION, !bThisElement);
		},

		setElementAction: function(/*DOMNode*/domNode, /*String*/idAction) {
			// summary: apply action to given node
			setDataAttribute(domNode, this.DATA_ACTION, idAction);
		},

		getElementContext: function(/*DOMNode*/domNode, /*Boolean?*/bThisElement) {
			// summary: return url applied to given domNode; if bThisElement not specified, then traverse parent nodes until url found
			return this.getDataAttribute(domNode, this.DATA_URL, !bThisElement);
		},

		setElementContext: function(/*DOMNode*/domNode, /*String*/urlNode) {
			// summary: apply url to given domNode
			setDataAttribute(domNode, this.DATA_URL, urlNode || "");
		},

		isDisabled: function(e) {
			return isWidget(e) ? e.get("disabled") : e.disabled;
		},

		setDisabled: function(e, b) {
			if (e && isWidget(e)) {
				e.set("disabled", b);
			} else if (e) {
				e.disabled = b;
			}
		},

		getPageWidth: function() {
			// summary: return width for page content, which will be full browser width unless printing
			if (_isPrinting) {
				return _printWidth;
			}

			// this seems to be the only way to get the correct browser width in IE
			if (document.compatMode && document.compatMode != "BackCompat")
				return document.documentElement.clientWidth;

			return win.body().clientWidth;
		},

		getTableWidth: function(data, max) {
			if (data) {
				var tableNode = create("table", {
					"cellspacing": 0,
					"border": 1,
					style: {width: "auto", "maxWidth": max ? max : "100%"}
				}, win.body());

				_.each(data, function(val, i) {
					var rowNode = create("tr", null, tableNode);

					if (_.isArray(val)) {
						_.each(val, function(s) {
							create("td", {"class": "dgrid-cell-padding", innerText: s}, rowNode);
						});
					} else {
						create("td", {"class": "dgrid-cell-padding", innerText: val}, rowNode);
					}
				});

				var actualWidth = tableNode.offsetWidth;
				tableNode.parentNode.removeChild(tableNode);
				return actualWidth;
			}
		},

		resetWidgetValue: function(/*_WidgetBase*/w, /*Any*/value) {
			// summary: change widget value without triggering onChange event
			w._onChangeActive = false;
			w.set("value", value);
			w._onChangeActive = true;
		},

		/**
		 * Converts hex color to rgb values
		 * @param {string} hex
		 * @return {array}
		 */
		hexToRGB: function(hex) {
			return (hex = hex.replace('#', '')).match(new RegExp('(.{' + hex.length / 3 + '})', 'g')).map(function(l) {return parseInt(hex.length % 2 ? l + l : l, 16)});
		},

		/**
		 * Converts rgb to hex color
		 * @param {number} r
		 * @param {number} g
		 * @param {number} b
		 * @return {string}
		 */
		rgbToHex: function(r, g, b) {
			return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).split('.')[0];
		},

		returnRichText: function(/*String*/s) {
			// summary: return sanitized rich text
			return this.sanitize(s).replace(/\\n/g, "<br>");
		},

		richText: function(/*String*/s, /*DOMNode*/eParent) {
			// summary: render rich text
			eParent.innerHTML = this.sanitize(s).replace(/\\n/g, "<br>");
		},

		/**
		 * Input an HTML string and sanitize it before returning it as text.
		 * @param {string} htmlString - string to sanitize and turn into text
		 */
		htmlToText: function(htmlString) {
			const sanitizedString = this.sanitize(htmlString).replace(/\n/g, "<br>");
			const div = document.createElement(DIV);
			div.innerHTML = sanitizedString;
			return div.textContent;
		},

		setInnerText: function(/*DOMNode*/eParent, /*String*/s, /*Boolean*/bNoWrap) {
			// summary: replaces current node's content with localized string
			// DEPRECATED: use text or multiLineText instead, and call appropriate method to clear
			// element if necessary; choices are domConstruct.empty() (if no widgets), destroyChildren() (if widgets),
			// or nothing at all
			if (eParent.firstChild) {
				clear(eParent, true);
			}

			text(eParent, s, bNoWrap);
		},

		textSeparator: function(/*String*/sep, /*DOMNode*/domNode, /*String?*/position) {
			// summary: add separator under domNode - if separator is actual text,
			// replace all line breaks with br tags
			var a = sep.replace(/\\n/g, "\n").split("\n");
			var n = a.length;
			var e;

			for (var i = 0; i < n; i++) {
				if (a[i]) {
					e = document.createTextNode(a[i]);
					domConstruct.place(e, domNode, position);
				}

				if (i < (n - 1)) {
					e = document.createElement("br");
					domConstruct.place(e, domNode, position);
				}
			}
		},

		unwrap: function(/*DOMNode*/eParent) {
			// summary: replace parent with all children
			var nl = query("> *", eParent);
			nl.place(eParent, "before");
			domConstruct.destroy(eParent);
			return nl;
		},

		/**
		 * If widget supports validation, then call the function to determine if it is valid; otherwise just return true
		 * @param {_WidgetBase} w
		 * @return {boolean}
		 */
		validateWidget: function(w) {
			if ((w.validate && !w.validate()) || (w.isValid && !w.isValid())) {
				return false;
			}

			return true;
		},

		input: function(type, name, value, eParent) {
			var input = domConstruct.create("input", {
				name: name || "",
				type: type || "text"
			});

			if (name) {
				input.setAttribute('id', name);
			}

			// add to DOM only after setting type
			if (eParent) {
				eParent.appendChild(input);
			}

			if (value != null) {
				input.setAttribute('value', value);
			}

			return input;
		},

		a: function(params, refNode, pos) {
			params = params || {};
			params.href = params.href || "javascript:;";
			return create("a", params, refNode, pos);
		},

		stopPropagation: function(ev) {
			if (!ev) {
				ev = window.event;
			}

			if (ev) {
				// IE
				ev.cancelBubble = true;

				// W3C
				if (ev.stopPropagation)
					ev.stopPropagation();
			}
		},

		getElementIndex: function(e) {
			if (!e)
				return null;

			var n = 0;

			while (e = e.previousSibling)
				++n;

			return n;
		},

		getInnerText: function(e) {
			if (!e) return null;

			var s = (typeof e === "string") ? e : e.innerHTML;
			if (!s) return "";

			s = s.replace(this.INNERTEXT_REGEXP, "");
			s = s.replace(/&nbsp;+/g, "");
			s = s.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

			// strip off comment indicator if it is there
			if (escape(s.charAt(s.length - 1)) == '%u2666') {
				s = s.substr(0, s.length - 1);
			}

			return purifyText(s);
		},

		elementHasTag: function(e, sTag) {
			if (e && e.tagName && (e.tagName.toUpperCase() == sTag))
				return true;
		},

		getParentByClassName: function(e, cls) {
			while (e && !domClass.contains(e, cls)) {
				e = e.parentNode;
			}

			return e;
		},

		getParentByTagName: function(e, sTag) {
			var s = sTag.toUpperCase();

			while (e && !this.elementHasTag(e, s)) {
				e = e.parentNode;
			}

			return e;
		},

		getDataAttribute: function(/*DOMNode*/domNode, /*String*/attrName, /*Boolean?*/bTraverseParents) {
			// summary: return value for given attribute on domNode, or on parent if bTraverseParents is true
			if (bTraverseParents) {
				domNode = this.getNodeWithDataAttribute(domNode, attrName);
			}

			var value = (domNode && domNode.getAttribute) ? domNode.getAttribute(DATA_CSI_PREFIX + attrName) : null;
			if (value != null) {
				// apply casting function if exists
				var cast = _dataAttributeCast[attrName];
				if (cast) {
					value = cast(value);
				}
			}

			return value;
		},

		getDataAttributeName,

		getDataAttributeSelector: function(/*String*/attrName, /*String?*/value, /*boolean?*/allowBackslash) {
			// summary: return text to use as selector for given attribute with optional value
			if (value != null) {
				// Check if the value is a string, if so call escapeDataAttributeSelectorValue
				if (typeof value == "string") {
					value = `="${escapeDataAttributeSelectorValue(value, allowBackslash)}"`;
				} else {
					value = `="${value}"`;
				}
			}

			return ["[", DATA_CSI_PREFIX, attrName, value, "]"].join("");
		},

		getDataAttributeValues: function(/*String*/attrName, /*DOMNode?*/rootNode) {
			// summary: return text to use as selector for given attribute with optional value
			return query(this.getDataAttributeSelector(attrName), rootNode).map(function(domNode) {
				return this.getDataAttribute(domNode, attrName);
			}, this);
		},

		getNodeWithDataAttribute: function(/*DOMNode*/domNode, /*String*/attrName, /*DOMNode?*/rootNode) {
			// summary: traverse up DOM structure until find node with attribute
			var v;

			while (domNode && (domNode != rootNode)) {
				v = domNode.getAttribute ? domNode.getAttribute(DATA_CSI_PREFIX + attrName) : null;

				if (v != null) {
					return domNode;
				}

				domNode = domNode.parentNode;
			}

			return v;
		},

		removeDataAttributeInTree: function(/*DOMNode*/rootNode, /*String*/attrName) {
			// summary: remove data attribute from all nodes under given node
			var s = DATA_CSI_PREFIX + attrName;

			this.removeDataAttribute(rootNode, attrName);

			query(this.getDataAttributeSelector(attrName), rootNode).forEach(function(domNode) {
				domNode.removeAttribute(s);
			});
		},

		removeRange: function(/*DOMNode*/eFirst, /*DOMNode?*/eLast, /*Function?*/filter) {
			// summary: fast removal of range of sibling nodes - does not destroy child widgets
			if (!eFirst) return;

			var eParent = eFirst.parentNode;
			var e;

			eLast = eLast || eParent.lastChild;

			while (eFirst && (eFirst != eLast)) {
				e = eFirst.nextSibling;

				if (!filter || filter(eFirst)) {
					eParent.removeChild(eFirst);
				}

				eFirst = e;
			}

			if (eLast && (!filter || filter(eLast))) {
				eParent.removeChild(eLast);
			}
		},

		inDOM: function(/*DOMNode*/domNode) {
			// summary: return true if domNode attached to DOM (in document flow)
			return domNode && dom.isDescendant(domNode, document.body);
		},

		/**
		 * Return true if element can be seen (has size and is visible)
		 * @param {HTMLElement} domNode 
		 * @param {boolean} [ignoreVisibility]
		 * @return {boolean}
		*/
		isShown(domNode, ignoreVisibility) {
			const style = domStyle.getComputedStyle(domNode);

			return !!domNode.offsetParent &&
				style.width !== 0 &&
				style.height !== 0 &&
				style.opacity !== 0 &&
				style.display !== "none" &&
				(ignoreVisibility || (style.visibility !== "hidden"));
		},

		isVisible: function(e) {
			if (e && isWidget(e)) {
				e = e.domNode;
			}

			return e && (domStyle.getComputedStyle(e).display != "none");
		},

		setVisible: function(e, b) {
			if (e && isWidget(e)) {
				e = e.domNode;
			}

			if (e && (e.nodeType == 1)) {
				domStyle.set(e, "display", b ? "" : "none");
			}
		},

		setCursorPosition: function(/*_WidgetBase*/w, /*Number?*/nStart, /*Number?*/nEnd) {
			// FIX: use dijit.selectInputText?
			var fn = w.focusNode;

			if (nStart == null) {
				var value = String(w.get("value"));
				if (value) {
					nStart = 0;
					nEnd = value.length;
				}
			}

			if (nStart == null) {
				w.focus();
			} else if (fn.setSelectionRange) {
				w.focus();
				fn.setSelectionRange(nStart, nEnd);
			} else if (fn.createTextRange) {
				var range = fn.createTextRange();
				range.moveStart("character", nStart);
				range.moveEnd("character", nEnd - nStart);
				range.select();
				w.focus();
			}
		},

		getCursorPosition: function(/*_WidgetBase*/w) {
			// summary: get cursor position inside input field
			var fn = w.focusNode;

			if (fn.selectionStart) {
				return fn.selectionStart;
			} else if (document.selection) {
				w.focus();

				var r = document.selection.createRange();
				if (r == null) {
					return 0;
				}

				var re = fn.createTextRange(),
					rc = re.duplicate();
				re.moveToBookmark(r.getBookmark());
				rc.setEndPoint("EndToStart", re);

				return rc.text.length;
			}

			return 0;
		},

		clearSelection: function() {
			// summary: clear selected text in window
			if (window.getSelection) {
				window.getSelection().removeAllRanges();
			} else if (document.selection) {
				document.selection.empty();
			}
		},

		/**
		 * Sanitize rich text to be able safely assign it to innerHTML; this function is needed when value comes from user input
		 * as it could be a copy and paste operation from our own HTML content (comment)
		 * @param {string} s 
		 * @return {string}
		 */
		sanitize(s) {
			s = s ? s.trim() : "";
			s = purifyForHtml(s);

			if (!s) {
				return s;
			}

			var domNode = domConstruct.toDom(s);
			if (!domNode) {
				return "";
			}

			// if plain text, then allow
			if (util.isTextNode(domNode)) {
				return s;
			}

			// if pasting rich text content from C8, then extract only rich text content
			try {
				var nl = query("." + CSS_RICH_TEXT, domNode);
				var n = nl.length;

				if (!n && domClass.contains(domNode, CSS_RICH_TEXT)) {
					nl.push(domNode);
					n = 1;
				}

				nl.forEach(function(node, i) {
					_sanitizeRichTextNode(node);
					_sanitizeDiv.appendChild(node);

					// if multiple rich text comments selected, then separate comments with line break
					if (i + 1 < n) {
						create("br", null, _sanitizeDiv);
					}
				});
			} catch (ex) {
				// ignore
			}

			if (!_sanitizeDiv.firstChild) {
				_sanitizeDiv.appendChild(domNode);
			}

			// clear root node for next time
			s = _sanitizeDiv.innerHTML;
			domConstruct.empty(_sanitizeDiv);

			return s;
		},

		/**
		 * This will make sure that offsetNode is fully visible inside domNode.
		 * Will only correct vertical position, not touching horizontal pos
		 * @param {DomNode}domNode - container node, node that do scroll (scrollTop position of this node will change while scrolling vertically)
		 * @param {DomNode}offsetNode - node to make visible
		 * @param {number} [bottomMargin] - margin on the bottom of scroll container
		 */
		scrollIntoViewVertically: function(domNode, offsetNode, bottomMargin) {
			const _bottomMargin = bottomMargin || 0;
			var scrollHeight = domNode.offsetHeight - _bottomMargin;
			var scrollTop = domNode.scrollTop;
			var nodeHeight = offsetNode.offsetHeight;
			var nodeTop = offsetNode.offsetTop;
			var nodeBottom = nodeHeight + nodeTop;
			var nodeOffset = nodeTop - scrollTop;
			var targetScrollTop;

			if ((nodeOffset <= 0) || (nodeHeight > scrollHeight)) {
				// Node top is above visible area
				targetScrollTop = nodeTop;
			} else if ((nodeOffset > scrollHeight) || (nodeBottom > (scrollHeight + scrollTop))) {
				// Node is fully below visible area || Node bottom is below visible area
				targetScrollTop = nodeBottom - scrollHeight;
			}

			if (targetScrollTop != null) {
				domNode.scrollTop = targetScrollTop;
			}
		},

		/**
		 * Cover body with transparent layer with progress cursor, preventing any input
		 * @param {bShow} [boolean] - if true, layer will be shown, otherwise layer will be destroyed
		 * @param {bTransparent} [boolean] - if true, layer will be transparent, no dots, no timeouts
		 * @param {bDelayed} [boolean] - if true, layer will be shown with delay, if not closed before
		 */
		showWait: function(bShow, bTransparent, bDelayed) {
			if (waitDiv) {
				if (!bShow) {
					// We want to allow 1.2 seconds for wait layer animation to play, to prevent flicker
					if (!showWaitTimeoutId) { // Animation is already done, there are no timeout
						destroyElement(waitDiv);
						waitDiv = null;
					} else { // Animation still playing, set flag to destroy element on animation end
						bShowWaitDestroy = true;
					}
				}
				return;
			} else if (bShow && bDelayed) {
				// Create timeout and start waiting before showing the layer
				if (!showWaitDelayTimeoutId) {
					showWaitDelayTimeoutId = setTimeout(() => {
						showWaitDelayTimeoutId = null;
						this.showWait(true, bTransparent)
					}, 500);
				}
				return;
			} else if (showWaitDelayTimeoutId) {
				// If showWait called without delay, or if close is called - clear delay timeout if present
				clearTimeout(showWaitDelayTimeoutId);
				showWaitDelayTimeoutId = null;
			}
			if (bShow) {
				showWaitTimeoutId = setTimeout(function destroyWaitDiv() {
					// Minimum time for wait layer animation passed, we don't need timer anymore
					if (bShowWaitDestroy) { // If wait layer was already requested to hide?
						destroyElement(waitDiv);
						waitDiv = null;
						bShowWaitDestroy = false;
					}
					showWaitTimeoutId = null;
				}, bTransparent ? 10 : 700);
				var cssClass = "csi-wait" + (bTransparent ? " csi-wait-transparent" : "");
				waitDiv = create('div', {'class': cssClass}, win.body());
				if (!bTransparent) {
					var div = create('div', {'class': 'csi-loading-circles'}, waitDiv);
					create('div', {'class': 'csi-loading-circle1'}, div);
					create('div', {'class': 'csi-loading-circle2'}, div);
					create('div', {'class': 'csi-loading-circle3'}, div);
					create('div', {'class': 'csi-loading-circle4'}, div);
				}
			}
		},

		clear,
		create,
		ctrlKeyPressed,
		destroyChildren,
		destroyElement,
		disablePageContentScrolling,
		download,
		elementOverflows,
		elementOverflowsWidth,
		focus,
		freezeDomNode,
		getAvailableHeight,
		getAvailableWidth,
		getAvailableHeightWithScrollbarAllowance,
		getBoundingRectAndPosition,
		getChildWidgets,
		getInputFileArray,
		getHorizontalScrollbarHeight,
		getVerticalScrollbarWidth,
		HEXtoRGB,
		hide,
		inlineCss,
		inVerticalScrollingContainer,
		isEmpty,
		isScrolledToBottom,
		isScrolledToTop,
		isScrollingHorizontally,
		isScrollingVertically,
		isWidget,
		multiLineText,
		multilineTextToArray,
		openWindowOnMouseClick,
		orphan,
		pageBottom,
		pageCenter,
		pageCenterSplit,
		pageContent,
		pageContentScroll,
		pageSplit,
		pixelsToStyleValue,
		pixelTo,
		preciseHeight,
		preciseWidth,
		removeDataAttribute,
		removeFocus,
		resizeChildren,
		selectFile,
		selectFiles,
		setCSSVariable,
		setDataAttribute,
		setPixels,
		setPixelsForChildren,
		setPrintStatus,
		setPrintWidth,
		setVisibility,
		show,
		text,
	};

	// register data attributes used in module
	_html.registerDataAttribute("DATA_ACTION", "act");
	_html.registerDataAttribute("DATA_URL", "url");

	// register data attributes used in HTML templates
	_html.registerDataAttribute("DATA_TEMPLATE_ACTIONS", "template-actions");
	_html.registerDataAttribute("DATA_TEMPLATE_ALIGN", "template-align", null, true);
	_html.registerDataAttribute("DATA_TEMPLATE_KEY", "template-key");
	_html.registerDataAttribute("DATA_TEMPLATE_LABEL", "template-label");
	_html.registerDataAttribute("DATA_TEMPLATE_RULE", "template-rule");

	return _html;
});
